import React, { useContext } from 'react';
import '../App.css';
import CSS from 'csstype';
import { useAppSelector } from '../app/hooks';

// types
import { ToolButtonType } from './tool-button';

// images

// classes

//	--------------------------------------------------------------------------
//
//	P R O P E R T I E S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	H T M L   C U S T O M   C O M P O N E N T S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	C L A S S   D E F I N I T I O N
//
//	--------------------------------------------------------------------------

//	------------------------------------------------------------
//
//	Parameters:
//
//	name: the div.name property
//	text: text to display to the left of the round component
//	abbreviatedText: text to display inside the round component
//	icon: icon to display inside the round component
//	count: a count value to display next to the round component
//	countOffset: true if the count is offset to the top of the button
//	selected: true if the button is selected (the round component
//			has a circle around it.
//	textSelected: true if the text is highlighted, and should
//			change colour.
//	onCLickHandler: the function to handle the button clicks.
//
//	------------------------------------------------------------

export default function RoundButton(props: {
  name?: string;
  text?: string;
  abbreviatedText?: string;
  icon?: string;
  LucideIcon?: any;
  count?: number;
  countOffset?: boolean;
  selected?: boolean;
  textSelected?: boolean;
  type?: ToolButtonType;
  onClickHandler?: any;
  darkBackground?: boolean;
  textOnRight?: boolean; // default FALSE
  bigButton?: boolean; // default FALSE
}) {
  //
  //	--------------------------------------------------------------------------
  //
  //	C O N T E X T   V A R I A B L E S   A N D   F U N C T I O N S
  //
  //	--------------------------------------------------------------------------

  //	--------------------------------------------------------------------------
  //
  //	R E D U X   S T A T E   H A N D L I N G
  //
  //	--------------------------------------------------------------------------

  // global state variables.
  const sG_DarkMode: boolean = useAppSelector((state) => state.global.darkMode);

  //	--------------------------------------------------------------------------
  //
  //	F U N C T I O N S
  //
  //	--------------------------------------------------------------------------

  //	------------------------------------------------------------
  //
  //	work out the border colour of a button.
  //
  //	------------------------------------------------------------

  function getOuterBorderColour() {
    //
    let colour: string = 'transparent';

    if (props.selected === true) {
      if (sG_DarkMode === true || props.darkBackground === true) {
        colour = 'white';
      } else {
        colour = 'black';
      }
    }

    return colour;
    //
  } // getOuterBorderColour

  //	------------------------------------------------------------
  //
  //	Get the border colour for the control, depending on
  //	the current theme, and whether this control is selected or
  //	not.
  //
  //	------------------------------------------------------------

  function getRoundBorderColour() {
    //
    let borderColour: string = 'transparent';

    if (props.selected === true && props.bigButton !== true) {
      if (props.darkBackground === true) {
        borderColour = 'white';
      } else {
        if (sG_DarkMode === true) {
          borderColour = 'white';
        } else {
          borderColour = '#0f1023';
        }
      }
    }

    return borderColour;
    //
  } // getRoundBorderColour

  //	--------------------------------------------------------------------------
  //
  //	C O M P O N E N T   C O D E
  //
  //	--------------------------------------------------------------------------

  //	------------------------------------------------------------
  //
  //	Display a count at the top of the round component.
  //
  //	------------------------------------------------------------

  function CountComponent() {
    //
    if (props.count !== undefined && props.count > 0) {
      return (
        <div
          className="user-control-notification-count"
          data-offset={props.countOffset === true ? 'T' : 'F'}
          data-dark-background={props.darkBackground === true ? 'T' : 'F'}
          style={{
            flex: '0 0 20px',
            width: '20px',
            height: '20px',
            borderRadius: '10px',
            fontSize: '7pt',
            display: 'flex',
            flexDirection: 'column',
            fontWeight: '800',
            textAlign: 'center',
            justifyContent: 'center',
            left:
              (props.countOffset || props.count === 0 || props.count === undefined) &&
              props.textOnRight !== true
                ? '7px'
                : '-12px',
          }}
        >
          {props.count > 99 ? '99+' : props.count}
        </div>
      );
    } else {
      return <div style={{ width: '20px', height: '20px' }}></div>;
    }
    //
  } // CountComponent

  //	------------------------------------------------------------
  //
  //	Display a round component with either an icon or some
  //	short text.
  //
  //	------------------------------------------------------------

  function RoundComponent() {
    //
    if (
      props.abbreviatedText !== undefined ||
      props.icon !== undefined ||
      props.LucideIcon !== undefined
    ) {
      return (
        <div
          className="background-colour text-colour"
          style={{
            flex: '0 0 ' + (props.bigButton === true ? '24px' : '30px'),
            height: props.bigButton === true ? '24px' : '30px',
            width: props.bigButton === true ? '24px' : '30px',
            borderRadius: props.bigButton === true ? '12px' : '15px',
            display: 'flex',
            flexDirection: 'column',
            fontSize: '10pt',
            fontWeight: '400',
            textAlign: 'center',
            justifyContent: 'center',
            alignItems: 'center',
            border: '1.5px solid ' + getRoundBorderColour(),
            position: 'relative',
            left:
              (props.countOffset || props.count === 0 || props.count === undefined) &&
              props.textOnRight !== true
                ? '20px'
                : '0px',
          }}
          data-invert="T"
        >
          {props.abbreviatedText !== undefined ? (
            props.abbreviatedText
          ) : props.icon !== undefined ? (
            <img src={props.icon} role="presentation" alt="" width="16px"></img>
          ) : props.LucideIcon !== undefined ? (
            <props.LucideIcon width={20} height={20} strokeWidth={1.5} />
          ) : (
            <></>
          )}
        </div>
      );
    } else {
      return <></>;
    }
    //
  } // RoundComponent

  //	------------------------------------------------------------
  //
  //	Display a text component to the left of the round
  //	component.
  //
  //	------------------------------------------------------------

  function TextComponent() {
    //
    if (props.text !== undefined) {
      return (
        <div
          className="text-colour"
          data-dark-background={props.darkBackground ? 'T' : 'F'}
          style={{
            flex: '0 0 auto',
            fontWeight: '400',
            fontSize: props.bigButton ? '1em' : '1.1em',
            position: 'relative',
            left:
              (props.countOffset || props.count === 0 || props.count === undefined) &&
              props.textOnRight !== true
                ? '20px'
                : '-8px',
          }}
          data-selected={
            props.selected === true || props.textSelected === true ? 'T' : 'F'
          }
        >
          {props.text}
        </div>
      );
    } else {
      return <></>;
    }
    //
  } // TextComponent

  let buttonStyle: object = {
    padding: '5px',
  };
  if (props.bigButton !== true) {
    buttonStyle = {
      ...buttonStyle,
      ...{
        flex: '0 0 auto',
        width: 'auto',
        height: '30px',
      },
    };
  }

  return (
    <button
      type="button"
      name={props.name}
      className="button-with-icon"
      data-type={
        props.type === ToolButtonType.PRIMARY
          ? 'PRIMARY'
          : props.type === ToolButtonType.SECONDARY
            ? 'SECONDARY'
            : props.type === ToolButtonType.TRANSPARENT
              ? 'TRANSPARENT'
              : props.type === ToolButtonType.NONE
                ? 'NONE'
                : 'NONE'
      }
      onClick={props.onClickHandler}
      style={buttonStyle}
    >
      {props.textOnRight !== true && <TextComponent />}
      {props.text !== undefined && props.textOnRight !== true && (
        <div style={{ flex: '0 0 10px', width: '10px' }} />
      )}
      <RoundComponent />
      <CountComponent />
      {props.textOnRight === true && <TextComponent />}
    </button>
  );
  //
} // ToolButton
